import { createAsyncThunk } from '@reduxjs/toolkit';
import { RECORD_STATUS, RECORD_TYPE } from 'LixaniAPI/enums';
import { RECORDS_ALLOW_MULTIPLE_CLOCK_IN_OWN_PROJECTS } from 'scenes/Permissions/constants/permissions';
import { selectUserAdminBusinessIds } from 'scenes/Permissions/store/selectors/base-project-permissions.selectors';
import { selectIsRequiredBusinessPermissionByBusinessId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { userJoinProject } from 'store/projects/thunks/userJoinProject.thunk';
import postClockInRecord from 'store/records/services/clockInRecord.service';
import {
  selectCurrentUserEmployerProjects,
  selectCurrentUserResourcesIndexedByProject,
  selectLoggedInUserPersonalResource,
  selectLoggedInUserResourcesArray,
} from 'store/resources/selectors/resources.selectors';
import { isResidentOnlyResources } from 'store/resources/utils/isResidentOnlyResources';
import {
  selectLoggedInUserId,
  selectUserActiveEmployerId,
  selectUserActiveEmployerInfo,
  selectUserCoordinates,
} from 'store/user/selectors.js/user.selectors';
import uuidv4 from 'uuid/v4';
import {
  openLocationMustDialog,
  openProfilePictureRequiredDialog,
  openProximityToClockInRequiredDialog,
  setOptimisticRecord,
  setProjectClockInLoading,
  setRecordAlreadyClockedInDialogOpen,
} from '../actions/records.actions';
import {
  openCardDialogWithUserId,
  setCardDialogRequiredCompetencyCardTypes,
} from 'store/cards/actions/cards.actions';
import { selectValidCurrentUserCards } from 'store/cards/selectors/user-cards.selectors';
import { getMissingRequiredCompetencyCardTypes } from 'store/cards/utils/requiredCompetencyCards';
import { selectPersonalClockedInWorkRecords } from '../selectors/records.selectors';
import { getGeolocation } from './getGeolocation.thunk';
import { calculateDistanceBetweenLocations } from 'utils/calculateDistanceBetweenLocations';
import { getProjectClockInAreas } from 'utils/getProjectClockInAreas';
import { isLocationInArea } from 'utils/isLocationInArea';

export const clockInRecord = createAsyncThunk(
  'records/clockInRecord',
  async (
    { projectId, allowClockInWithoutLocation },
    { getState, dispatch, rejectWithValue }
  ) => {
    const projects = selectProjects(getState());

    const project = projects[projectId];

    // Residents must never clock in: guarding here covers every clock-in
    // surface (project lists, timer drawer, project page, retry dialogs).
    const currentUserProjectResources =
      selectCurrentUserResourcesIndexedByProject(getState())[projectId];

    if (isResidentOnlyResources(currentUserProjectResources)) {
      return;
    }

    const alreadyClockedInWorkRecords =
      selectPersonalClockedInWorkRecords(getState());
    if (alreadyClockedInWorkRecords && alreadyClockedInWorkRecords.length > 0) {
      const userEmployerProjects =
        selectCurrentUserEmployerProjects(getState());

      const isPermissionForMultipleRecords =
        selectIsRequiredBusinessPermissionByBusinessId(getState(), {
          businessId: project.business_id,
          permission: RECORDS_ALLOW_MULTIPLE_CLOCK_IN_OWN_PROJECTS,
        });

      // in the future, check currently active employer for defined restriction
      const isEmployerWithMultpleClockInRestriction = userEmployerProjects.some(
        (employerProject) => employerProject.restrictEmployeesMultipleClockIn
      );

      if (
        isEmployerWithMultpleClockInRestriction &&
        !isPermissionForMultipleRecords
      ) {
        dispatch(setRecordAlreadyClockedInDialogOpen(true));

        return;
      }
    }

    if (project.pictureMust) {
      const currentUserPersonalResource =
        selectLoggedInUserPersonalResource(getState());

      if (currentUserPersonalResource && !currentUserPersonalResource.picture) {
        dispatch(openProfilePictureRequiredDialog());
        return;
      }
    }

    // Per-project only: enforce this project's own required competency cards.
    const requiredCompetencyCardTypes = project.competenciesRequired
      ? project.requiredCompetencyCardTypes || []
      : [];
    if (requiredCompetencyCardTypes.length > 0) {
      // Admins of the clock-in project's business may skip the competency
      // card requirement entirely.
      const isAdminInClockInProject = selectUserAdminBusinessIds(
        getState()
      ).includes(project.business_id);

      if (!isAdminInClockInProject) {
        const validCards = selectValidCurrentUserCards(getState());
        const missingRequiredCompetencyCardTypes =
          getMissingRequiredCompetencyCardTypes(
            validCards,
            requiredCompetencyCardTypes
          );

        if (missingRequiredCompetencyCardTypes.length > 0) {
          // Open the card dialog directly with the missing competencies shown
          // at the top, so the user can add them without an extra dialog hop.
          dispatch(
            setCardDialogRequiredCompetencyCardTypes(
              missingRequiredCompetencyCardTypes
            )
          );
          dispatch(openCardDialogWithUserId(selectLoggedInUserId(getState())));
          return;
        }
      }
    }

    dispatch(setProjectClockInLoading(projectId));

    const now = new Date().toISOString();

    await dispatch(getGeolocation());

    const activeEmployerInfo = selectUserActiveEmployerInfo(getState());

    const activeEmployerId = selectUserActiveEmployerId(getState());

    const employerProjects = selectCurrentUserEmployerProjects(getState());

    const activeEmployerProject = employerProjects.find(
      (project) => project.id === activeEmployerId
    );

    const loggedInUserId = selectLoggedInUserId(getState());

    const currentUserResources = selectLoggedInUserResourcesArray(getState());

    const userAdminBusinessIds = selectUserAdminBusinessIds(getState());

    const currentUserResourcesFromRecordProject = currentUserResources.filter(
      (resource) => resource.project === projectId
    );

    const isAdminInClockInProject = userAdminBusinessIds.includes(
      project.business_id
    );

    let resourceToMarkRecordFor;

    if (
      currentUserResourcesFromRecordProject.length === 0 &&
      isAdminInClockInProject
    ) {
      // for admin users, automatic join to project if no existing resource for project
      await dispatch(
        userJoinProject({
          projectKey: project.projectKey,
          businessId: project.business_id,
        })
      );
      const updatedCurrentUserResources =
        selectLoggedInUserResourcesArray(getState());

      const updatedCurrentUserResourcesFromRecordProject =
        updatedCurrentUserResources.filter(
          (resource) => resource.project === projectId
        );

      resourceToMarkRecordFor = returnEffectiveResourceToClockInRecordFor(
        updatedCurrentUserResourcesFromRecordProject,
        undefined,
        activeEmployerProject
      );
    } else {
      resourceToMarkRecordFor = returnEffectiveResourceToClockInRecordFor(
        currentUserResourcesFromRecordProject,
        activeEmployerId,
        activeEmployerProject
      );
    }

    // if no resource can be deduced from front end, resource is sent as undefined to backend, where it is finally determined
    // also setting billedClockInAt default value same as clockInAt

    const coordinates = selectUserCoordinates(getState());

    console.log('coordinates:', coordinates);

    let recordWithActiveEmployerResourceFilledAndBilledClockOut = {
      id: uuidv4(),
      clockInAt: now,
      project: project.id,
      status: RECORD_STATUS.IN,
      type: RECORD_TYPE.WORK,
      title: null,
      mirrorUser: loggedInUserId,

      resource: resourceToMarkRecordFor
        ? resourceToMarkRecordFor.id
        : undefined,
      billedClockInAt: now,
      latIn: coordinates ? coordinates.latitude : undefined,
      longIn: coordinates ? coordinates.longitude : undefined,
      user: loggedInUserId,
    };

    // Early exit if location is required but missing
    if (project.locationMust && !coordinates && !allowClockInWithoutLocation) {
      dispatch(openLocationMustDialog(project));
      return rejectWithValue(
        recordWithActiveEmployerResourceFilledAndBilledClockOut
      );
    }

    // Handle proximity requirement
    if (project.locationMust && project.proximityToClockInRequired) {
      console.log('project proximity requirement active');

      // If no coordinates available at all
      if (!coordinates) {
        dispatch(openProximityToClockInRequiredDialog(project));
        return rejectWithValue(
          recordWithActiveEmployerResourceFilledAndBilledClockOut
        );
      }

      let isOutsideProximity = false;
      if (project.proximityToClockInType === 'radius') {
        const distance = calculateDistanceBetweenLocations(coordinates, {
          latitude: project.latitude,
          longitude: project.longitude,
        });

        console.log('distance to project:', distance);

        const maxDistance = project.proximityToClockInRadius || 100;
        isOutsideProximity = distance > maxDistance;
      } else if (project.proximityToClockInType === 'area') {
        const areas = getProjectClockInAreas(project);
        const isInArea =
          areas.length > 0 &&
          areas.some((area) => isLocationInArea(coordinates, area.positions));
        console.log('is in proximity area:', isInArea);
        isOutsideProximity = !isInArea;
      }

      if (isOutsideProximity) {
        if (!allowClockInWithoutLocation) {
          dispatch(openProximityToClockInRequiredDialog(project));
          return rejectWithValue(
            recordWithActiveEmployerResourceFilledAndBilledClockOut
          );
        }

        console.log('proceeding with clock in even if outside proximity');
        recordWithActiveEmployerResourceFilledAndBilledClockOut = {
          ...recordWithActiveEmployerResourceFilledAndBilledClockOut,
          locationNotInProximity: true,
        };
      }
    }

    dispatch(
      setOptimisticRecord(
        recordWithActiveEmployerResourceFilledAndBilledClockOut
      )
    );

    try {
      const response = await postClockInRecord(
        recordWithActiveEmployerResourceFilledAndBilledClockOut,
        activeEmployerInfo
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        recordWithActiveEmployerResourceFilledAndBilledClockOut
      );
    }
  }
);

const returnEffectiveResourceToClockInRecordFor = (
  currentUserProjectResources,
  activeEmployerProjectId,
  activeEmployerProject
) => {
  const recordProjectResourcesWithNavigationActiveEmployer =
    currentUserProjectResources.filter(
      (resource) =>
        (activeEmployerProjectId &&
          activeEmployerProjectId === resource.employer) ||
        (activeEmployerProject &&
          activeEmployerProject?.business_id === resource.employerBusinessId)
    );

  const resourceToMarkRecordFor =
    recordProjectResourcesWithNavigationActiveEmployer[0];

  return resourceToMarkRecordFor;
};
